import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, H3, Hero, In, List, P, Related, Src, Steps, Table, WhatIs, WorkflowCard } from "@/components/seo/kit";

// /integrations/outlook-to-teams. Plan: gtm/products/clearmakes-inbox/seo/pages/outlook-to-teams.md
// The Microsoft ways come first because for many Microsoft 365 users they are enough.
// Facts opened on 2026-09-29; sources are linked on the page.

const PATH = "/integrations/outlook-to-teams";

const faqs = [
  {
    q: "What is the simplest way to post Outlook emails to a Teams channel?",
    a: "Get the channel's email address in Teams (More options, then Get email address) and send or forward emails to it. It needs your IT admin to have the feature switched on, and it copies the email rather than choosing or summarising it.",
  },
  {
    q: "Is Power Automate free for this?",
    a: "Often, yes. Microsoft says basic Power Automate is included at no cost for work or school accounts, limited to standard connectors, and both the Outlook and Teams connectors are standard. You build the flow yourself from a trigger and an action.",
  },
  {
    q: "Can a shared mailbox post to Teams?",
    a: "In Power Automate, use the trigger “When a new email arrives in a shared mailbox (V2)”. In ClearMakes Inbox, connect an account that can open the mailbox; whether a particular shared mailbox can be watched depends on how it is set up in Microsoft 365.",
  },
  {
    q: "When is ClearMakes Inbox worth it over Power Automate?",
    a: "When you would rather describe the job in one sentence than build a flow, when you want a summary or a check before posting, or when the same workflow also needs Gmail, Slack, HubSpot or a wait for a reply.",
  },
  {
    q: "What does it cost?",
    a: "Free covers 3 connected apps, 2 new workflows a month and 100 runs a month. Starter is $29 a month for 5,000 runs. Each email that starts the workflow is one run, whatever number of steps it has.",
  },
];

const OutlookToTeams = () => (
  <>
    <PageMeta
      title="Post Outlook Emails to a Teams Channel Automatically"
      description="Three ways to post Outlook emails to a Microsoft Teams channel: the channel address, Power Automate, or a one-sentence ClearMakes Inbox workflow."
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Integrations", path: "/integrations" },
        { name: "Outlook to Teams", path: PATH },
      ]}
    />
    <main>
      <Hero
        eyebrow="Integrations · Outlook and Microsoft Teams"
        title="Post Outlook emails to a Teams channel automatically"
        lead="Three ways to do it, the free Microsoft ones first, and when a one-sentence ClearMakes Inbox workflow is the better fit."
        secondary={{ label: "See every integration", href: "/integrations" }}
      />

      <WhatIs title="What is the Outlook to Teams workflow in ClearMakes Inbox?">
        It is a ClearMakes Inbox workflow that watches an Outlook mailbox and, when a new email matches what you described, posts it to a
        Microsoft Teams channel, often with a short summary. You describe it in one sentence and check the steps it writes. There is a free
        plan; paid plans start at $29 a month.
      </WhatIs>

      <Band
        tone="pale"
        title="Way 1: the Teams channel email address"
        answer="Every Teams channel can have its own email address, and anything sent to it appears in the channel. It is free, but your IT admin must have switched it on, and it copies emails rather than choosing them."
      >
        <Steps
          items={[
            { title: "Open the channel in Teams on the desktop or the web", body: "The mobile app cannot show the address." },
            { title: "Choose More options, then Get email address", body: "Copy the address it shows." },
            { title: "Send or forward emails to it", body: "An Outlook rule can forward matching emails. Your organisation's settings decide whether automatic forwarding is allowed." },
          ]}
        />
        <P>
          Limits Microsoft lists: up to 20 attachments, each up to 10 MB, and up to 50 inline images; not available on Office 365 Government
          plans; blocked when the address is used inside a distribution list. Source:{" "}
          <Src href="https://support.microsoft.com/en-us/office/send-an-email-to-a-channel-in-microsoft-teams-d91db004-d9d7-4a47-82e6-fb1b16dfd51e">Microsoft Support, send an email to a channel</Src>, checked 29 September 2026.
        </P>
      </Band>

      <Band
        tone="white"
        title="Way 2: a Power Automate flow"
        answer="Power Automate can post each new email to a channel, and for work or school accounts the basic version is included at no cost. You build it from a trigger and an action, and you choose the fields yourself."
      >
        <Steps
          items={[
            { title: "Create an automated cloud flow", body: "Trigger: “When a new email arrives (V3)”, or “When a new email arrives in a shared mailbox (V2)” for a shared mailbox." },
            { title: "Add the Teams action", body: "“Post message in a chat or channel”, then pick the team and channel." },
            { title: "Choose what the message shows", body: "Add the sender, subject and body fields. Filtering or summarising needs extra steps, and some of those need a Premium licence." },
          ]}
        />
        <P>
          Both connectors are standard, and Microsoft says the free licence covers “cloud flows with standard connectors”. Power Automate
          Premium is $15 a user a month, paid yearly. Sources, checked 29 September 2026:{" "}
          <Src href="https://learn.microsoft.com/en-us/power-platform/admin/power-automate-licensing/types">Power Automate licence types</Src>,{" "}
          <Src href="https://learn.microsoft.com/en-us/connectors/office365/">Office 365 Outlook connector</Src>,{" "}
          <Src href="https://learn.microsoft.com/en-us/connectors/teams/">Microsoft Teams connector</Src>,{" "}
          <Src href="https://www.microsoft.com/en-us/power-platform/products/power-automate/pricing">Power Automate pricing</Src>.
        </P>
      </Band>

      <Band
        tone="pale"
        title="Way 3: describe it in one sentence with ClearMakes Inbox"
        answer="Type what should happen and Inbox writes the steps: which emails count, what to post, and where. It suits teams that want a summary or a check, or that also use Gmail, Slack or HubSpot."
      >
        <div className="grid gap-5 lg:grid-cols-2">
          <WorkflowCard
            sentence="When the sales@ mailbox gets a new enquiry, post the company, what they want and a one-line summary to the Sales channel in Teams."
            steps={["When an email arrives in Outlook", "Pull out the details: company, request", "Summarise it", "Post to the Sales channel in Teams"]}
          />
          <WorkflowCard
            sentence="When a supplier emails an invoice, post the supplier, amount and due date to the Finance channel in Teams."
            steps={["When an email arrives in Outlook", "Stop if it is not an invoice", "Pull out the details: supplier, amount, due date", "Post to the Finance channel in Teams"]}
          />
        </div>
        <H3>Set it up</H3>
        <Steps
          items={[
            { title: "Create a free ClearMakes account", body: <>Start from <In href="/products/inbox">ClearMakes Inbox</In>.</> },
            { title: "Connect Outlook and Microsoft Teams", body: "Open Apps and connect each one with Microsoft's sign-in. A work account may need your admin to approve the app." },
            { title: "Describe the workflow and check the steps", body: "Type the sentence, answer at most one question, then create the workflow and switch it on." },
          ]}
        />
      </Band>

      <Band
        tone="white"
        title="Which way should you use?"
        answer="If you only need a copy of certain emails in a channel, use the channel address or Power Automate. Pick Inbox when you want the choosing, summarising and follow-up done for you."
      >
        <Table
          caption="Ways to post Outlook emails to Teams, compared"
          head={["", "Channel email address", "Power Automate", "ClearMakes Inbox"]}
          rows={[
            ["Cost", "Free with Teams", "Free licence for work or school accounts (standard connectors); Premium $15 a user a month, yearly", "Free plan (100 runs a month); Starter $29 a month"],
            ["Set-up", "Copy an address, add a rule", "Build a flow from a trigger and actions", "One sentence, then check the steps"],
            ["Choose which emails", "With Outlook rules", "With conditions you add", "Described in the sentence"],
            ["Summary in the post", "No", "Extra steps", "Yes, if you ask"],
            ["Wait for the sender's reply, then act", "No", "Possible with more steps", "Built-in step"],
            ["Other apps in the same workflow", "No", "Many connectors", "Gmail, Slack, HubSpot, Notion and thousands more"],
            ["Needs admin", "Admin must switch the feature on", "Depends on your organisation", "Admin approval for the app on some work accounts"],
          ]}
          note="Microsoft facts checked on 29 September 2026 (sources above). ClearMakes Inbox prices from /pricing."
        />
      </Band>

      <Band tone="pale" title="Limits, and who this is not for" answer="If your whole company is on Microsoft 365 and the job is a simple copy, Power Automate's free licence is the natural choice. Inbox is not trying to replace it." narrow>
        <List
          items={[
            "Not a Teams bot: Inbox posts to channels; replies to the email still go from Outlook.",
            "AI checks and summaries can be wrong now and then; look at the first runs.",
            "Government (GCC) tenants: not tested.",
          ]}
        />
      </Band>

      <Faqs items={faqs} />

      <Related
        links={[
          { href: "/integrations/outlook-to-slack", label: "Outlook to Slack", desc: "The same idea for teams on Slack." },
          { href: "/teams-integration", label: "Microsoft Teams automation", desc: "Everything a workflow can post to Teams." },
          { href: "/outlook-integration", label: "Outlook email automation", desc: "What a workflow can do with a new Outlook email." },
          { href: "/integrations/email-to-hubspot", label: "Email leads to HubSpot", desc: "Contacts from new enquiries, filled in." },
          { href: "/for-remote-teams", label: "For remote teams", desc: "Email to the right channel across time zones." },
          { href: "/pricing", label: "ClearMakes Inbox pricing", desc: "Free, Starter, Growth and Enterprise." },
        ]}
      />

      <Closing title="Post the Outlook emails that matter to Teams." line="Connect Outlook and Teams, type one sentence, and check the steps." />
    </main>
    <Footer />
  </>
);

export default OutlookToTeams;
